import React from "react";
import { Link } from "react-router-dom";
import { Navigation } from "./Navigation";
import SEO from "./SEO";
import Footer from "./Footer";

export default function TermsPage() {
  const termsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Terms of Service | One80Labs",
    description:
      "One80Labs Terms of Service - Learn about our AI training, fractional project management, and custom AI tool development services in Omaha, Nebraska.",
    url: "https://one80labs.com/terms",
    mainEntity: {
      "@type": "Article",
      headline: "Terms of Service | One80Labs",
      description:
        "Terms of Service for One80Labs AI training, development, and fractional project management services.",
      author: {
        "@type": "Organization",
        name: "One80Labs",
      },
      publisher: {
        "@type": "Organization",
        name: "One80Labs",
        address: {
          "@type": "PostalAddress",
          streetAddress: "4383 Nicholas Street #302",
          addressLocality: "Omaha",
          addressRegion: "NE",
          postalCode: "68181",
          addressCountry: "US",
        },
      },
      datePublished: "2025-01-08",
      dateModified: "2025-01-08",
    },
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "One80Labs",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    url: "https://one80labs.com",
    logo: "https://one80labs.com/logo.png",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+1-402-915-3616",
      contactType: "customer service",
      email: "hello@one80labs.com",
      areaServed: "US",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://one80labs.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Terms of Service",
        item: "https://one80labs.com/terms",
      },
    ],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "One80Labs",
    url: "https://one80labs.com",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://one80labs.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "One80Labs",
    description:
      "AI Training & Fractional Project Management in Omaha, Nebraska",
    url: "https://one80labs.com",
    telephone: "+1-402-915-3616",
    email: "hello@one80labs.com",
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 41.2651675,
      longitude: -95.9397187,
    },
    openingHours: "Mo-Fr 09:00-17:00",
    areaServed: [
      {
        "@type": "City",
        name: "Omaha",
      },
      {
        "@type": "City",
        name: "Council Bluffs",
      },
      {
        "@type": "State",
        name: "Nebraska",
      },
      {
        "@type": "State",
        name: "Iowa",
      },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "AI Training & Project Management Services",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "AI Training",
            description: "Custom AI training for businesses and non-profits",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Fractional Project Management",
            description: "Part-time project management services",
          },
        },
      ],
    },
  };

  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Nick Kramer",
    jobTitle: "AI Productivity Specialist & Technology Educator",
    worksFor: {
      "@type": "Organization",
      name: "One80Labs",
    },
    knowsAbout: [
      "AI Implementation",
      "Project Management",
      "Non-profit Technology",
      "Omaha Business",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "4383 Nicholas Street #302",
      addressLocality: "Omaha",
      addressRegion: "NE",
      postalCode: "68181",
      addressCountry: "US",
    },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Terms of Service | One80Labs - AI Training & Fractional PM Services"
        description="One80Labs Terms of Service - Learn about our AI training, fractional project management, and custom AI tool development services in Omaha, Nebraska."
        keywords={[
          "terms of service",
          "One80Labs terms",
          "AI training terms",
          "fractional PM terms",
          "Omaha business terms",
          "Nebraska service terms",
          "Midwest AI services",
          "legal terms",
          "service agreement",
          "business terms",
        ]}
        image="https://one80labs.com/logo.png"
        article={true}
        author="One80Labs"
        publishedTime="2025-01-08T00:00:00.000Z"
        modifiedTime="2025-01-08T00:00:00.000Z"
        section="Legal"
        tags={[
          "terms of service",
          "legal",
          "AI training",
          "fractional PM",
          "Omaha",
          "Nebraska",
        ]}
      />

      <Navigation />

      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1
              className="text-4xl font-bold text-gray-900 mb-4"
              id="main-heading"
            >
              Terms of Service
            </h1>
            <p className="text-xl text-gray-600" role="doc-subtitle">
              Effective Date: January 8, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Terms of Service Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          <p className="text-lg text-gray-700 mb-8">
            Welcome to One80Labs! These Terms of Service ("Terms") govern your
            use of our{" "}
            <Link to="/" className="text-[#F98B3D] hover:underline">
              website
            </Link>
            , services, and products. By accessing or using our site or
            services, you agree to these Terms. If you do not agree, please do
            not use our website or services.
          </p>

          <p className="text-lg text-gray-700 mb-8">
            We are headquartered in{" "}
            <Link to="/contact" className="text-[#F98B3D] hover:underline">
              Omaha, Nebraska
            </Link>{" "}
            and proudly serve clients throughout the Midwest and beyond.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="services-provided"
          >
            1. Services We Provide
          </h2>
          <p className="text-gray-700 mb-4">One80Labs specializes in:</p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>AI Training & Workshops (in-person and virtual)</li>
            <li>Custom AI Tool Development</li>
            <li>
              Fractional Project Management Services (including AI project
              management)
            </li>
          </ul>
          <p className="text-gray-700 mb-6">
            The details, pricing, and scope of our services are outlined in
            separate proposals, statements of work, or service agreements.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="eligibility"
          >
            2. Eligibility
          </h2>
          <p className="text-gray-700 mb-6">
            You must be at least 18 years old and able to enter into legally
            binding agreements to use our services. By using our site or
            services, you confirm that you meet these requirements.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="website-use"
          >
            3. Use of Our Website
          </h2>
          <p className="text-gray-700 mb-4">You agree to:</p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>Use our site for lawful purposes only.</li>
            <li>
              Not engage in unauthorized data mining, scraping, or reverse
              engineering.
            </li>
            <li>
              Not interfere with the security or functionality of our site.
            </li>
          </ul>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="intellectual-property"
          >
            4. Intellectual Property
          </h2>
          <p className="text-gray-700 mb-4">
            All content on www.one80labs.com, including text, images, graphics,
            videos, and logos, is the property of One80Labs or its content
            providers and is protected by copyright and trademark laws.
          </p>
          <p className="text-gray-700 mb-6">
            You may not copy, reproduce, distribute, or create derivative works
            without written permission.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="payments-billing"
          >
            5. Payments & Billing
          </h2>
          <p className="text-gray-700 mb-4">
            If you purchase services from us:
          </p>
          <ul className="list-disc pl-6 mb-6 text-gray-700">
            <li>Payment terms will be outlined in your agreement.</li>
            <li>
              All invoices are due according to the payment schedule provided.
            </li>
            <li>
              Late payments may result in service delays or additional fees.
            </li>
          </ul>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="cancellations-refunds"
          >
            6. Cancellations & Refunds
          </h2>
          <p className="text-gray-700 mb-6">
            Cancellation and refund policies vary by service and will be
            outlined in your individual agreement or registration terms for
            events and training.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="disclaimer-warranties"
          >
            7. Disclaimer of Warranties
          </h2>
          <p className="text-gray-700 mb-6">
            Our website and services are provided "as is" without warranties of
            any kind, either express or implied. While we strive for accuracy,
            we do not guarantee that the site will be error-free or
            uninterrupted.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="limitation-liability"
          >
            8. Limitation of Liability
          </h2>
          <p className="text-gray-700 mb-4">
            To the fullest extent permitted by law, One80Labs is not liable for
            any indirect, incidental, or consequential damages arising from your
            use of our website or services.
          </p>
          <p className="text-gray-700 mb-6">
            Our total liability for any claim will not exceed the amount paid
            for the specific service giving rise to the claim.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="privacy"
          >
            9. Privacy
          </h2>
          <p className="text-gray-700 mb-6">
            Your privacy is important to us. Please review our{" "}
            <Link to="/privacy" className="text-[#F98B3D] hover:underline">
              Privacy Policy
            </Link>{" "}
            to understand how we collect, use, and protect your information.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="governing-law"
          >
            10. Governing Law
          </h2>
          <p className="text-gray-700 mb-6">
            These Terms are governed by the laws of the State of Nebraska,
            without regard to its conflict of law provisions.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="changes-terms"
          >
            11. Changes to These Terms
          </h2>
          <p className="text-gray-700 mb-6">
            We may update these Terms at any time. Updates will be posted here
            with a revised "Effective Date." Your continued use of the site and
            services after changes means you accept the updated Terms.
          </p>

          <h2
            className="text-2xl font-bold text-gray-900 mt-12 mb-6"
            id="contact-us"
          >
            12. Contact Us
          </h2>
          <p className="text-gray-700 mb-4">
            If you have questions about these Terms, contact us:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <p className="text-gray-700 font-semibold">One80Labs</p>
            <p className="text-gray-700">Omaha, Nebraska</p>
            <p className="text-gray-700">
              📧 Email:{" "}
              <a
                href="mailto:hello@one80labs.com"
                className="text-[#F98B3D] hover:underline"
              >
                hello@one80labs.com
              </a>
            </p>
            <p className="text-gray-700">
              🌐 Website:{" "}
              <a
                href="https://www.one80labs.com"
                className="text-[#F98B3D] hover:underline"
              >
                www.one80labs.com
              </a>
            </p>
          </div>
        </div>
      </div>

      <Footer />

      {/* Schema Markup */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(termsSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(localBusinessSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(personSchema),
        }}
      />
    </div>
  );
}
